import { defineStore } from 'pinia';
import type {
  AuditEntry,
  CommittedExtraction,
  ConflictFlag,
  DraftValidationError,
  DraftFieldOverride,
  EvidenceCategory,
  EvidenceItem,
  BloodPressureOptions,
  NumericOptions,
  ObservationInputType,
  ObservationStatus,
  ObservationType,
  ObservationValidationCode,
} from '../types/text-mode.types';

const {
  createObservationWarningFingerprint,
  validateBloodPressure,
  validateObservationValue,
} = require('@shared/clinical-logic/helpers/observationRangeValidation.js');

type DraftValidationSeverity = 'normal' | 'warning' | 'blocking' | 'unconfigured';

interface DraftValidationResult {
  severity: DraftValidationSeverity;
  code: 'normal' | ObservationValidationCode;
  value: string | number | boolean | null;
  messageToken?: string;
  parts?: {
    systolic: { severity: DraftValidationSeverity; code: 'normal' | ObservationValidationCode };
    diastolic: { severity: DraftValidationSeverity; code: 'normal' | ObservationValidationCode };
  };
}

interface DraftValidationMetadata {
  observationType?: ObservationType;
  input?: ObservationInputType;
  numericOptions?: NumericOptions | null;
  bloodPressureOptions?: BloodPressureOptions | null;
}

export const READ_ONLY_ITEM_IDS = new Set([
  'patient-age',
  'patient-gender',
  'patient-maternal',
  'patient-maternal-status',
]);

export interface DraftStoreState {
  originalExtraction: Record<string, string | number | boolean | null>;
  originalItemNames: Record<string, string>;
  draftOverrides: Record<string, DraftFieldOverride>;
  committed: CommittedExtraction;
  auditTrail: AuditEntry[];
  conflicts: ConflictFlag[];
  validationErrors: Record<string, DraftValidationError>;
  validationMetadata: Record<string, DraftValidationMetadata>;
  validationResults: Record<string, DraftValidationResult>;
  presentedWarningFingerprints: Record<string, string>;
  isEvidenceEditMode: boolean;
  isRhsSubmitting: boolean;
}

export type DraftStoreSnapshot = Omit<DraftStoreState, 'committed'> & {
  committed: Omit<CommittedExtraction, 'incorrectIds'> & { incorrectIds: string[] };
};

function createEmptyCommitted(): CommittedExtraction {
  return {
    values: {},
    incorrectIds: new Set<string>(),
    observationStatuses: {},
  };
}

function itemLabel(item: EvidenceItem): string {
  return item.nameToken ?? item.name;
}

function withoutKey<T>(record: Record<string, T>, key: string): Record<string, T> {
  const next = { ...record };
  delete next[key];
  return next;
}

function validationErrorFor(
  metadata: DraftValidationMetadata,
  result: DraftValidationResult,
): DraftValidationError {
  if (metadata.input === 'blood_pressure') {
    return { token: 'questions.blood_pressure.message.error' };
  }
  if (result.code === 'invalid_format') {
    return { token: 'app.assessment_mode.number.input.error.invalid_number' };
  }
  return {
    token: 'app.assessment_mode.number.input.error.out_of_range',
    params: metadata.numericOptions
      ? { min: metadata.numericOptions.min, max: metadata.numericOptions.max }
      : undefined,
  };
}

function validateDraftValue(
  metadata: DraftValidationMetadata | undefined,
  value: string | number | boolean | null,
): DraftValidationResult | null {
  if (!metadata) return null;
  if (metadata.input === 'blood_pressure') {
    return validateBloodPressure(metadata.bloodPressureOptions, value) as DraftValidationResult;
  }
  if (metadata.observationType !== 'number') return null;
  if (typeof value === 'string'
    && value.trim().toLowerCase() === 'unknown'
    && metadata.numericOptions?.allowUnknown) return null;
  return validateObservationValue(metadata.numericOptions, value) as DraftValidationResult;
}

function warningFingerprint(
  itemId: string,
  metadata: DraftValidationMetadata,
  value: string | number | boolean | null,
): string {
  return createObservationWarningFingerprint({ itemId, metadata }, value);
}

export const useDraftStore = defineStore('textModeDraft', {
  state: (): DraftStoreState => ({
    originalExtraction: {},
    originalItemNames: {},
    draftOverrides: {},
    committed: createEmptyCommitted(),
    auditTrail: [],
    conflicts: [],
    validationErrors: {},
    validationMetadata: {},
    validationResults: {},
    presentedWarningFingerprints: {},
    isEvidenceEditMode: false,
    isRhsSubmitting: false,
  }),

  getters: {
    displayValueFor:
      (state) =>
      (itemId: string): string | number | boolean | null => {
        if (state.draftOverrides[itemId]) {
          return state.draftOverrides[itemId].draftValue;
        }
        return state.originalExtraction[itemId] ?? null;
      },

    isDrafted: (state) => (itemId: string): boolean =>
      Boolean(state.draftOverrides[itemId]),

    isMarkedIncorrect: (state) => (itemId: string): boolean =>
      Boolean(state.draftOverrides[itemId]?.markedIncorrect),

    isRemoved: (state) => (itemId: string): boolean =>
      Boolean(state.draftOverrides[itemId]?.markedIncorrect),

    isCommittedIncorrect: (state) => (itemId: string): boolean =>
      state.committed.incorrectIds.has(itemId),

    activeConflicts: (state): ConflictFlag[] =>
      state.conflicts.filter((c) => !c.dismissed),

    hasDraftChanges: (state): boolean =>
      Object.keys(state.draftOverrides).length > 0,

    hasValidationErrors: (state): boolean =>
      Object.keys(state.validationErrors).length > 0,

    validationFor: (state) => (itemId: string): DraftValidationResult | null =>
      state.validationResults[itemId] ?? null,

    isWarningPresented: (state) => (itemId: string): boolean => {
      const result = state.validationResults[itemId];
      const metadata = state.validationMetadata[itemId];
      const override = state.draftOverrides[itemId];
      if (result?.severity !== 'warning' || !metadata || !override) return false;
      return state.presentedWarningFingerprints[itemId]
        === warningFingerprint(itemId, metadata, override.draftValue);
    },
  },

  actions: {
    setEvidenceEditMode(isEvidenceEditMode: boolean): void {
      this.isEvidenceEditMode = isEvidenceEditMode;
    },

    setRhsSubmitting(isRhsSubmitting: boolean): void {
      this.isRhsSubmitting = isRhsSubmitting;
    },

    buildDraftFromTranscriptSubmit(categories: EvidenceCategory[]): void {
      const original: Record<string, string | number | boolean | null> = {};
      const names: Record<string, string> = {};
      const metadata: Record<string, DraftValidationMetadata> = {};

      for (const cat of categories) {
        for (const item of cat.items) {
          original[item.id] = item.value;
          names[item.id] = itemLabel(item);
          metadata[item.id] = {
            observationType: item.observationType,
            input: item.input,
            numericOptions: item.numericOptions,
            bloodPressureOptions: item.bloodPressureOptions,
          };
        }
      }

      this.originalExtraction = original;
      this.originalItemNames = names;
      this.validationMetadata = metadata;
      this.draftOverrides = {};
      this.conflicts = [];
      this.validationErrors = {};
      this.validationResults = {};
      this.presentedWarningFingerprints = {};
      this.isEvidenceEditMode = false;
      this.isRhsSubmitting = false;
    },

    updateDraftField(
      itemId: string,
      value: string | number | boolean | null,
    ): void {
      if (READ_ONLY_ITEM_IDS.has(itemId)) return;

      const existing = this.draftOverrides[itemId] ?? {
        itemId,
        draftValue: null,
        markedIncorrect: false,
      };

      this.draftOverrides = {
        ...this.draftOverrides,
        [itemId]: { ...existing, draftValue: value, markedIncorrect: false },
      };

      this._validateDraftField(itemId, value);
      this._refreshConflicts();
    },

    updateDraftConceptState(itemId: string, status: ObservationStatus): void {
      if (READ_ONLY_ITEM_IDS.has(itemId)) return;

      const existing = this.draftOverrides[itemId] ?? {
        itemId,
        draftValue: null,
        markedIncorrect: false,
      };

      this.draftOverrides = {
        ...this.draftOverrides,
        [itemId]: {
          ...existing,
          observationStatus: status,
          markedIncorrect: false,
        },
      };

      this._refreshConflicts();
    },

    removeDraftObservation(itemId: string): void {
      if (READ_ONLY_ITEM_IDS.has(itemId)) return;

      const existing = this.draftOverrides[itemId] ?? {
        itemId,
        draftValue: null,
        markedIncorrect: false,
      };

      this.draftOverrides = {
        ...this.draftOverrides,
        [itemId]: { ...existing, markedIncorrect: true },
      };
      const { [itemId]: _error, ...validationErrors } = this.validationErrors;
      this.validationErrors = validationErrors;
      this.validationResults = withoutKey(this.validationResults, itemId);
      this.presentedWarningFingerprints = withoutKey(
        this.presentedWarningFingerprints,
        itemId,
      );
      this._refreshConflicts();
    },

    markDraftConceptIncorrect(itemId: string): void {
      this.removeDraftObservation(itemId);
    },

    revertDraftField(itemId: string): void {
      const { [itemId]: _removed, ...rest } = this.draftOverrides;
      this.draftOverrides = rest;
      const { [itemId]: _error, ...validationErrors } = this.validationErrors;
      this.validationErrors = validationErrors;
      this.validationResults = withoutKey(this.validationResults, itemId);
      this.presentedWarningFingerprints = withoutKey(
        this.presentedWarningFingerprints,
        itemId,
      );
      this._refreshConflicts();
    },

    setDraftValidationError(itemId: string, error: DraftValidationError | null): void {
      if (!error) {
        const { [itemId]: _removed, ...rest } = this.validationErrors;
        this.validationErrors = rest;
        return;
      }

      this.validationErrors = {
        ...this.validationErrors,
        [itemId]: error,
      };
    },

    prepareWarningsForSubmit(): boolean {
      if (this.hasValidationErrors) return false;

      let revealedWarning = false;
      const nextFingerprints = { ...this.presentedWarningFingerprints };
      const validationEntries = Object.entries(this.validationResults) as Array<[
        string,
        DraftValidationResult,
      ]>;
      for (const [itemId, result] of validationEntries) {
        const metadata = this.validationMetadata[itemId];
        const override = this.draftOverrides[itemId];
        if (result.severity !== 'warning' || !metadata || !override) continue;

        const fingerprint = warningFingerprint(itemId, metadata, override.draftValue);
        if (nextFingerprints[itemId] === fingerprint) continue;
        nextFingerprints[itemId] = fingerprint;
        revealedWarning = true;
      }

      this.presentedWarningFingerprints = nextFingerprints;
      return !revealedWarning;
    },

    commitDraftToEvidenceChanges(): AuditEntry[] {
      const now = new Date().toISOString();
      const newEntries: AuditEntry[] = [];

      const newValues: Record<string, string | number | boolean | null> = {
        ...this.committed.values,
      };
      const newIncorrect = new Set(this.committed.incorrectIds);
      const newStatuses: Record<string, ObservationStatus> = {
        ...this.committed.observationStatuses,
      };

      for (const [itemId, overrideVal] of Object.entries(this.draftOverrides)) {
        const override = overrideVal as DraftFieldOverride;
        const originalValue = this.originalExtraction[itemId] ?? null;

        const effectiveCorrectedValue: string | number | boolean | null =
          override.markedIncorrect
            ? null
            : override.draftValue;

        const entry: AuditEntry = {
          itemId,
          fieldName: this.originalItemNames[itemId] ?? itemId,
          originalValue,
          correctedValue: effectiveCorrectedValue,
          markedIncorrect: override.markedIncorrect,
          committedAt: now,
        };

        newEntries.push(entry);

        if (override.markedIncorrect) {
          newIncorrect.add(itemId);
          delete newValues[itemId];
          delete newStatuses[itemId];
        } else {
          newIncorrect.delete(itemId);
          if (override.draftValue !== null && override.draftValue !== undefined) {
            newValues[itemId] = override.draftValue;
          }
          if (override.observationStatus) {
            newStatuses[itemId] = override.observationStatus;
          }
        }
      }
      this.committed = {
        values: newValues,
        incorrectIds: newIncorrect,
        observationStatuses: newStatuses,
      };

      this.auditTrail = [...this.auditTrail, ...newEntries];

      this.draftOverrides = {};
      this.validationErrors = {};
      this.validationResults = {};
      this.presentedWarningFingerprints = {};

      this.conflicts = this.conflicts.map((c) => ({ ...c, dismissed: true }));

      return newEntries;
    },

    commitDraftToClinicalLogic(): AuditEntry[] {
      return this.commitDraftToEvidenceChanges();
    },

    dismissConflict(itemId: string): void {
      this.conflicts = this.conflicts.map((c) =>
        c.itemId === itemId ? { ...c, dismissed: true } : c,
      );
    },

    dismissAllConflicts(): void {
      this.conflicts = this.conflicts.map((c) => ({ ...c, dismissed: true }));
    },

    resetDraft(): void {
      this.draftOverrides = {};
      this.conflicts = [];
      this.validationErrors = {};
      this.validationResults = {};
      this.presentedWarningFingerprints = {};
      this.isEvidenceEditMode = false;
      this.isRhsSubmitting = false;
    },

    fullReset(): void {
      this.originalExtraction = {};
      this.originalItemNames = {};
      this.draftOverrides = {};
      this.committed = createEmptyCommitted();
      this.auditTrail = [];
      this.conflicts = [];
      this.validationErrors = {};
      this.validationMetadata = {};
      this.validationResults = {};
      this.presentedWarningFingerprints = {};
      this.isEvidenceEditMode = false;
      this.isRhsSubmitting = false;
    },

    resume(payload: DraftStoreSnapshot): void {
      Object.assign(this, {
        ...payload,
        committed: {
          ...payload.committed,
          incorrectIds: new Set(payload.committed?.incorrectIds ?? []),
        },
        isEvidenceEditMode: false,
        isRhsSubmitting: false,
      });
    },

    _refreshConflicts(): void {
      const updated: ConflictFlag[] = [];

      for (const [itemId, overrideVal] of Object.entries(this.draftOverrides)) {
        const override = overrideVal as DraftFieldOverride;
        if (override.markedIncorrect) continue;

        const originalValue = this.originalExtraction[itemId] ?? null;

        const correctedValue: string | number | boolean | null = override.draftValue;
        const hasConflict =
          originalValue !== null &&
          correctedValue !== null &&
          String(correctedValue) !== String(originalValue);

        if (hasConflict) {
          const existing = this.conflicts.find((c) => c.itemId === itemId);
          updated.push({
            itemId,
            fieldName: this.originalItemNames[itemId] ?? itemId,
            transcriptValue: originalValue,
            correctedValue,
            dismissed: existing?.dismissed ?? false,
          });
        }
      }

      const resolvedIds = new Set(updated.map((c) => c.itemId));
      const kept = this.conflicts.filter(
        (c) => resolvedIds.has(c.itemId) || c.dismissed,
      );
      const merged = new Map<string, ConflictFlag>();
      for (const c of kept) merged.set(c.itemId, c);
      for (const c of updated) merged.set(c.itemId, c);

      this.conflicts = Array.from(merged.values());
    },

    _validateDraftField(
      itemId: string,
      value: string | number | boolean | null,
    ): void {
      const metadata = this.validationMetadata[itemId];
      const result = validateDraftValue(metadata, value);
      if (!result || result.severity === 'normal') {
        this.validationResults = withoutKey(this.validationResults, itemId);
        this.validationErrors = withoutKey(this.validationErrors, itemId);
        this.presentedWarningFingerprints = withoutKey(
          this.presentedWarningFingerprints,
          itemId,
        );
        return;
      }

      result.messageToken = result.severity === 'warning'
        ? 'questions.generic.message.abnormal'
        : validationErrorFor(metadata, result).token;
      this.validationResults = { ...this.validationResults, [itemId]: result };

      if (result.severity === 'warning') {
        this.validationErrors = withoutKey(this.validationErrors, itemId);
        const fingerprint = warningFingerprint(itemId, metadata, value);
        if (this.presentedWarningFingerprints[itemId] !== fingerprint) {
          this.presentedWarningFingerprints = withoutKey(
            this.presentedWarningFingerprints,
            itemId,
          );
        }
        return;
      }

      this.validationErrors = {
        ...this.validationErrors,
        [itemId]: validationErrorFor(metadata, result),
      };
      this.presentedWarningFingerprints = withoutKey(
        this.presentedWarningFingerprints,
        itemId,
      );
    },
  },
});
